import React, { useEffect } from 'react';
import DataModelerFieldsGridHeader from './DataModelerFieldsGridHeader';
import * as FormUI from '../../../pageForm/FormUI';
import DataModelerAddedFieldRow from './DataModelerAddedFieldRow';
import DataModelerExistingFieldRow from './DataModelerExistingFieldRow';
import {
  DERIVED_FIELD_EDIT_FORM_ANCHOR_ID,
  useDataModelFieldsGridModel,
} from './utils';
import DataModelerDraftDerivedFieldRow from './DataModelerDraftDerivedFieldRow';
import { useAppSelector } from '../../../../redux/hooks';
import { dataModelerSelectCurrentUiState } from '../../../../redux/selectors/dataModeler';
import DataModelerEditDerivedField from './DataModelerEditDerivedField';
import { useDataModelerContext } from '../../DataModelerContext';

interface IProps {
  searchQuery: string;
}

const DataModelerFieldsGrid = ({ searchQuery }: IProps) => {
  const uiState = useAppSelector(dataModelerSelectCurrentUiState);

  const {
    sortedList,
    sortColumn,
    sortOrderOrNull,
    fieldItemMatches,
    onColumnClickFn,
    schemaObj,
  } = useDataModelFieldsGridModel();

  const { isEditMode } = useDataModelerContext();

  const editedDerivedFieldKey =
    uiState?.draftDerivedField?.fieldMeta?.key ??
    uiState?.draftDerivedField?.newFieldKey ??
    null;
  const editDerivedFieldComponentKey =
    editedDerivedFieldKey ?? 'new-derived-field';

  // scroll to the derived field form if the form is about to show up to edit already mapped column
  useEffect(() => {
    if (!!editedDerivedFieldKey) {
      const anchorEl = document.getElementById(
        DERIVED_FIELD_EDIT_FORM_ANCHOR_ID,
      );
      if (anchorEl) {
        anchorEl.scrollIntoView();
      }
    }
  }, [editedDerivedFieldKey]);

  return (
    <>
      {uiState?.draftDerivedField && (
        <DataModelerEditDerivedField
          key={editDerivedFieldComponentKey}
          draft={uiState.draftDerivedField}
        />
      )}
      <FormUI.Container $noMarginTop={true}>
        <DataModelerFieldsGridHeader
          onColumnClickFn={onColumnClickFn}
          sortColumn={sortColumn}
          sortOrderOrNull={sortOrderOrNull}
        />
        {sortedList.map((item) => {
          if (!fieldItemMatches(searchQuery, item)) {
            return null;
          }
          const {
            isRequired,
            originalIndex,
            addFieldInput,
            editFieldInput,
            fieldMeta,
            derivedFieldInput,
          } = item;
          return addFieldInput ? (
            <DataModelerAddedFieldRow
              key={`add-${addFieldInput.key}-${originalIndex}`}
              field={addFieldInput}
              isRequired={isRequired}
              schemaKey={schemaObj.key}
              extendInputs={item.extendFieldInputs}
            />
          ) : derivedFieldInput ? (
            <DataModelerDraftDerivedFieldRow
              key={`draft-${derivedFieldInput.key}`}
              field={derivedFieldInput}
            />
          ) : (
            <DataModelerExistingFieldRow
              key={`edit-${fieldMeta!!.key}`}
              schemaKey={schemaObj.key}
              fieldMeta={fieldMeta!!}
              editInput={editFieldInput}
              isEditMode={isEditMode}
              extendInputs={item.extendFieldInputs}
            />
          );
        })}
      </FormUI.Container>
    </>
  );
};

export default DataModelerFieldsGrid;
